/**
 * Dashboard sound-trigger hook (BUILD_SPEC.md §16.1, Milestone 7 step 57).
 *
 * Watches each successful dashboard poll for *fresh* keyword-bearing
 * actions and fires the corresponding tone exactly once per keyword
 * per poll cycle. Compared with naively reacting to every action with
 * `containsKeyword`, this hook:
 *
 *   • Skips the very first poll, so just-loading-the-page never fires
 *     a chime — “new” is relative to a baseline the user has already
 *     seen (criterion: “do not play sounds on initial dashboard load”).
 *   • De-duplicates by keyword (criterion: three new APPROVAL actions
 *     in one poll play one chime, not three).
 *   • Maintains a `seenActionIds` set across polls so an action that
 *     persists with `isNew=true` for multiple cycles only triggers once
 *     on the cycle it first appeared.
 *   • Still updates the seen-set while the tab is hidden so that
 *     unhiding doesn’t replay a backlog of chimes.
 *
 * The hook is intentionally read-only against the React Query cache —
 * it doesn’t schedule polls or own retry logic, it only reacts to the
 * data the page already has.
 */

import { useEffect, useRef } from "react";
import type { KeywordType } from "../../../../packages/keywords/index.js";
import type { Action, Transaction } from "@/api/types";
import { useSoundStore } from "@/stores/soundStore";
import { playKeywordSound } from "@/lib/sounds";

interface DashboardItem {
  transaction: Transaction;
  actions: Action[];
}

interface Options {
  /** The dashboard's current item list. May be `undefined` while the
   *  query is loading; the hook simply skips those renders. */
  items: DashboardItem[] | undefined;
  /** True after the first poll has resolved. Provided by the caller
   *  so we don't recompute it inside the hook — the React Query
   *  states are the source of truth. */
  ready: boolean;
}

export function useDashboardSounds({ items, ready }: Options): void {
  const enabled = useSoundStore((s) => s.enabled);
  const perKeyword = useSoundStore((s) => s.perKeyword);
  const volume = useSoundStore((s) => s.volume);

  // Action IDs the dashboard has already observed. `null` until the
  // first poll has been processed so we can distinguish “never seen
  // any data yet” from “seen an empty list before.”
  const seenRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!ready || !items) return;

    // Collect the action IDs that count as “in scope” for the chime
    // logic — the latest three actions per card with a keyword set.
    // The spec narrows the trigger window this way so a backlog of
    // historical keyword actions doesn’t chime when a transaction is
    // first added (its scraper backfill returns dozens of rows, only
    // the first three of which appear on the card).
    const inScope: Array<{ id: string; keyword: KeywordType }> = [];
    for (const item of items) {
      const top3 = item.actions.slice(0, 3);
      for (const a of top3) {
        if (a.isNew && a.containsKeyword) {
          inScope.push({ id: a.id, keyword: a.containsKeyword });
        }
      }
    }

    if (seenRef.current === null) {
      // First poll after mount. Bootstrap the seen-set with whatever
      // is currently visible and intentionally skip the chime pass.
      seenRef.current = new Set(inScope.map((a) => a.id));
      return;
    }

    const seen = seenRef.current;
    const freshKeywords = new Set<KeywordType>();
    for (const { id, keyword } of inScope) {
      if (!seen.has(id)) {
        freshKeywords.add(keyword);
        seen.add(id);
      }
    }

    // Play once per unique keyword. Order is fixed (REJECTION
    // → APPROVAL → ASSIGNMENT) so when multiple keywords fire on the
    // same cycle the higher-priority chime starts first — matches
    // the §10.3 card-keyword precedence.
    const ORDER: KeywordType[] = ["REJECTION", "APPROVAL", "ASSIGNMENT"];
    for (const k of ORDER) {
      if (freshKeywords.has(k)) {
        playKeywordSound(k, {
          masterEnabled: enabled,
          perKeywordEnabled: perKeyword,
          volume,
        });
      }
    }
  }, [items, ready, enabled, perKeyword, volume]);
}
